import {
  buildScrapersConsultation,
  createFailedConsultation,
  createPendingConsultation,
  createScraperConsultationItem,
  formatBrlPrice,
  parseBrlAmount,
  sumPassagePrices,
  ScraperConsultationItem,
  ScraperItemStatus,
  ScraperPassage,
  ScrapersConsultation,
} from "@/domain/scrapers/consultation";
import environment from "@/settings/environment";

export const SCRAPERS_CONSULTATION_PATH =
  "/freeflow/obterpassagemconc/v2/consultar-por-placa";

const simulationByPlate = new Map<string, number>();
const requestIdByPlate = new Map<string, string>();

const SUCCESS_STATUS = "CONSULTADO_COM_SUCESSO";
const PENDING_STATUS = "PENDENTE";
const FINISHED_STATUSES = new Set(["FINALIZADO", "FINALIZADO_COM_FALHAS"]);

export function resetScrapersSimulation(plate?: string): void {
  if (plate) {
    const normalized = normalizePlate(plate);
    simulationByPlate.delete(plate);
    simulationByPlate.delete(normalized);
    requestIdByPlate.delete(plate);
    requestIdByPlate.delete(normalized);
    return;
  }
  simulationByPlate.clear();
  requestIdByPlate.clear();
}

const SIMULATION_CONCESSIONAIRES = [
  { id: "tamoios_v2", name: "Tamoios" },
  { id: "eprpedagioeletronico_v2", name: "EPR" },
  { id: "pedagiodigital_v2", name: "Pedágio Digital" },
  { id: "way262_v2", name: "Way 262" },
];

export function simulateScrapersConsultation(
  plate: string,
): ScrapersConsultation {
  const current = simulationByPlate.get(plate) ?? 0;
  const next = Math.min(current + 1, SIMULATION_CONCESSIONAIRES.length);
  simulationByPlate.set(plate, next);
  const items = SIMULATION_CONCESSIONAIRES.map((concessionaire, index) =>
    createScraperConsultationItem({
      id: concessionaire.id,
      name: concessionaire.name,
      status: index < next ? "empty" : "pending",
    }),
  );
  return buildScrapersConsultation(items, {
    total: SIMULATION_CONCESSIONAIRES.length,
    isComplete: next === SIMULATION_CONCESSIONAIRES.length,
  });
}

function normalizePlate(plate: string): string {
  return plate.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
}

function unwrapData(payload: unknown): Record<string, unknown> | undefined {
  const root = asRecord(payload);
  if (!root) return undefined;
  const data = asRecord(root.data);
  if (!data) return root;
  const nested = asRecord(data.data);
  if (nested && (nested.resultado || nested.resultados || nested.statusGeral)) {
    return nested;
  }
  return data;
}

export function extractRequestId(payload: unknown): string | undefined {
  if (typeof payload === "string" && payload.trim()) return payload.trim();
  const root = asRecord(payload);
  if (!root) return undefined;
  if (typeof root.requestId === "string") return root.requestId;
  if (typeof root.data === "string" && root.data.trim())
    return root.data.trim();
  const data = asRecord(root.data);
  if (!data) return undefined;
  if (typeof data.requestId === "string") return data.requestId;
  if (typeof data.data === "string" && data.data.trim())
    return data.data.trim();
  return undefined;
}

function flattenConsultaResults(items: unknown[]): Record<string, unknown>[] {
  return items.flatMap((item) => {
    const record = asRecord(item);
    if (!record) return [];
    if (Array.isArray(record.resultado)) {
      return record.resultado.map(asRecord).filter(Boolean) as Record<
        string,
        unknown
      >[];
    }
    return [record];
  });
}

function extractResultado(
  data: Record<string, unknown>,
): Record<string, unknown>[] {
  if (Array.isArray(data.consultas)) {
    return flattenConsultaResults(data.consultas);
  }
  if (Array.isArray(data.resultado)) {
    return data.resultado.map(asRecord).filter(Boolean) as Record<
      string,
      unknown
    >[];
  }
  if (!Array.isArray(data.resultados)) return [];
  return flattenConsultaResults(data.resultados);
}

function formatPrice(value: unknown): string {
  const amount = parseBrlAmount(value);
  if (amount === undefined) {
    return typeof value === "string" && value.trim() ? value : "0,00";
  }
  return formatBrlPrice(amount);
}

function formatDatePart(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length < 10) return undefined;
  const isoDate = value.slice(0, 10);
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return undefined;
  return `${day}.${month}.${year}`;
}

function formatTurno(value: unknown): string | undefined {
  if (typeof value !== "string" || !value.trim()) return undefined;
  const key = value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
  if (key.includes("MANHA")) return "Manhã";
  if (key.includes("TARDE")) return "Tarde";
  if (key.includes("NOITE")) return "Noite";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function mapPassage(
  raw: unknown,
  fallbackLocation: string,
): ScraperPassage | undefined {
  const passage = asRecord(raw);
  if (!passage) return undefined;
  const location =
    (typeof passage.praca === "string" && passage.praca) ||
    (typeof passage.local === "string" && passage.local) ||
    (typeof passage.concessionaria === "string" && passage.concessionaria) ||
    fallbackLocation;
  const datePart = formatDatePart(passage.data ?? passage.dataPassagem);
  const turno = formatTurno(passage.turno);
  const dateLabel = [datePart, turno].filter(Boolean).join(" ");
  return {
    location,
    dateLabel: dateLabel || fallbackLocation,
    price: formatPrice(passage.valorTotal ?? passage.valor),
  };
}

function isFinishedStatus(statusGeral: unknown): boolean {
  return typeof statusGeral === "string" && FINISHED_STATUSES.has(statusGeral);
}

function mapPaymentUrl(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!/^https?:\/\//i.test(trimmed)) return undefined;
  return trimmed;
}

function mapItemStatus(
  statusConsulta: string,
  finished: boolean,
  hasPassages: boolean,
): ScraperItemStatus {
  if (!statusConsulta || statusConsulta === PENDING_STATUS) {
    return finished ? "error" : "pending";
  }
  if (statusConsulta === SUCCESS_STATUS) {
    return hasPassages ? "with_passages" : "empty";
  }
  return "error";
}

function mapConsultationItem(
  item: Record<string, unknown>,
  finished: boolean,
): ScraperConsultationItem | undefined {
  const apiId =
    typeof item.idConcessionaria === "string" && item.idConcessionaria.trim()
      ? item.idConcessionaria.trim()
      : undefined;
  const name =
    typeof item.concessionaria === "string" && item.concessionaria.trim()
      ? item.concessionaria.trim()
      : apiId;
  if (!apiId && !name) return undefined;

  const id = apiId ?? name ?? "";
  const displayName = name ?? id;
  const statusConsulta =
    typeof item.statusConsulta === "string" ? item.statusConsulta : "";
  const passages = Array.isArray(item.passagens)
    ? (
        item.passagens
          .map((passage) => mapPassage(passage, displayName))
          .filter(Boolean) as ScraperPassage[]
      ).map((passage) => ({
        ...passage,
        concessionaireName: displayName,
      }))
    : [];
  const hasPassages = item.possuiPassagem === true || passages.length > 0;

  return createScraperConsultationItem({
    id,
    name: displayName,
    status: mapItemStatus(statusConsulta, finished, hasPassages),
    paymentUrl: mapPaymentUrl(item.urlOficial),
    passages,
    totalPrice: sumPassagePrices(passages),
  });
}

function extractProgresso(data: Record<string, unknown>): {
  total?: number;
  completedCount?: number;
} {
  const progresso = asRecord(data.progresso);
  if (!progresso) return {};
  return {
    total: typeof progresso.total === "number" ? progresso.total : undefined,
    completedCount:
      typeof progresso.finalizadas === "number"
        ? progresso.finalizadas
        : undefined,
  };
}

export function mapScrapersStatusResponse(
  payload: unknown,
): ScrapersConsultation {
  const root = asRecord(payload);
  if (root?.success === false) return createFailedConsultation();

  const data = unwrapData(payload);
  if (!data) return createFailedConsultation();

  const finished = isFinishedStatus(data.statusGeral);
  const items: ScraperConsultationItem[] = [];
  const seenIds = new Set<string>();

  for (const item of extractResultado(data)) {
    const mapped = mapConsultationItem(item, finished);
    if (!mapped) continue;
    if (seenIds.has(mapped.id)) {
      const index = items.findIndex((current) => current.id === mapped.id);
      items[index] = mapped;
      continue;
    }
    seenIds.add(mapped.id);
    items.push(mapped);
  }

  const progresso = extractProgresso(data);

  return buildScrapersConsultation(items, {
    total: progresso.total,
    completedCount: progresso.completedCount,
    isComplete: finished,
  });
}

function getScrapersRequestConfig() {
  return {
    baseURL: environment.api.scrapersBaseUri,
    headers: {
      Accept: "application/json",
      "x-api-key": environment.api.scrapersApiKey,
    },
  };
}

export async function fetchScrapersConsultation(
  plate: string,
): Promise<ScrapersConsultation> {
  const normalizedPlate = normalizePlate(plate);
  if (!normalizedPlate) return createPendingConsultation();

  try {
    const { getApiConfig } = await import("@/settings/api");
    const { apiInstance } = getApiConfig(false);
    const requestConfig = getScrapersRequestConfig();

    let requestId = requestIdByPlate.get(normalizedPlate);
    if (!requestId) {
      const startResponse = await apiInstance.post(
        SCRAPERS_CONSULTATION_PATH,
        { placas: [normalizedPlate] },
        requestConfig,
      );
      requestId = extractRequestId(startResponse.data);
      if (!requestId) return createFailedConsultation();
      requestIdByPlate.set(normalizedPlate, requestId);
    }

    const statusResponse = await apiInstance.get(
      `${SCRAPERS_CONSULTATION_PATH}/${requestId}`,
      requestConfig,
    );
    const consultation = mapScrapersStatusResponse(statusResponse.data);
    if (consultation.isComplete) {
      requestIdByPlate.delete(normalizedPlate);
    }
    return consultation;
  } catch {
    requestIdByPlate.delete(normalizedPlate);
    return createFailedConsultation();
  }
}
